import BigNumber from 'bignumber.js';
import debounce from 'lodash/debounce';
import Service from 'react-reservice';

import { EMainProductCodeType } from '@webtrader/core/api/config/company';
import { ISymbol } from '@webtrader/core/api/market/symbols';
import symbolDetailAPI, { ISymbolDetail } from '@webtrader/core/api/market/symbols/detail';
import orderAPI, { EOrderHistoryType, IHistoryDateRange, IOrder, IOrderHistory } from '@webtrader/core/api/order';
import dealHistories, { IDealHistory } from '@webtrader/core/api/order/dealHistory';
import pendingAPI, { IPendingOrder } from '@webtrader/core/api/order/pending';
import traderAPI, {
  ETradeFillPolicy,
  IBulkDeletePendingRequest,
  ICloseMarketTradeParams
} from '@webtrader/core/api/trade';
import {
  EAssetsOrderTab,
  EOrderActionType,
  EOrderProfitStatus,
  EOrderSortOption,
  EOrderTypeLabel,
  ISortOrderOption,
  TAssetsOrderTab
} from '@webtrader/core/constants/order';
import dayjs from '@webtrader/core/dayjs';
import { mapOrderProfitStatusToBulkCloseType } from '@webtrader/core/format/order';
import orderBy from '@webtrader/core/format/orderBy';
import { removeThousandSeparator } from '@webtrader/core/format/price';
import i18n from '@webtrader/core/i18n';
import { IApiError } from '@webtrader/core/request/api';
import { mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { mixpanelAnalysisService } from '@webtrader/core/services/analysis';
import { chartService } from '@webtrader/core/services/chart';
import { configService } from '@webtrader/core/services/config';
import { EAppType } from '@webtrader/core/services/config/service';
import storage, { EStorageKey } from '@webtrader/core/storage';

import { companyService } from '../company';
import { statisticsService } from '../statistics';
import { convertDateRangeWithUtcOffset } from './utils';

export type TOrder = IOrder | IPendingOrder;

interface IOrderContext {
  orders: IOrder[];
  pendingOrders: IPendingOrder[];
  orderHistories: IOrderHistory[];
  orderHistoriesCentroid: IOrderHistory[];
  ordersAndDealsHistories: IDealHistory[];
  topListingOrders: IOrder[];
  orderTypeLabel: EOrderTypeLabel | null;
  orderActionType: EOrderActionType | null;
  historyDateRange: IHistoryDateRange;
  loadingDetails: boolean;
  loadingTopListing: boolean;
  loadingBulkClose: boolean;
  selectedSort: ISortOrderOption;
  selectedSymbolDetail?: ISymbolDetail;
  historyType: EOrderHistoryType;
  displayOrders: IOrder[];
  displayPendingOrders: IPendingOrder[];
  marketOrderCounts: number;
  pendingOrderCounts: number;
  isTradePage?: boolean;
  showAllSymbols?: boolean;
  openOrdersInitialCall?: boolean;
  pendingOrdersInitialCall?: boolean;
  historyOrdersInitialCall?: boolean;
  historyDealsInitialCall?: boolean;
  historyOrdersCentroidInitialCall?: boolean;
  // This flag is used to disable the refresh of the chart with updated orders from websocket. It is set to true when the order tracker is being dragged on the chart.
  // This is to prevent instability due to the delay in chart data updates during the drag operation.
  disableUpdateTrackOrder: boolean;
}

interface ICloseMarketOrderParams extends ICloseMarketTradeParams {
  onComplete?: (success: boolean, message: string) => void;
}

export interface ICloseOrderParams {
  onComplete: (success: boolean, message: string) => void;
  mixpanelProperties?: {
    livePrice: number | number;
  };
}

export interface ICloseLimitOrderParams {
  order: IPendingOrder;
  onComplete?: (success: boolean, message: string) => void;
  mixpanelProperties?: {
    livePrice: number | undefined;
  };
}

export interface ISoftRefreshOrdersParams {
  signal?: AbortSignal;
  sort?: ISortOrderOption;
  disableUpdateTrackOrder?: boolean;
}

interface ICalculateRiskRewardsParams {
  potentialGain?: string;
  potentialLoss?: string;
}

interface IWebSocketOrBulkCloseRefreshOrdersParams {
  refreshOpenOrders?: boolean;
  refreshOrderHistory?: boolean;
}

const getDefaultHistoryDateRange = () => ({
  to: dayjs().endOf('day').toDate(),
  from: dayjs().subtract(1, 'months').startOf('day').toDate()
});

const defaultHistoryDateRange: IHistoryDateRange = getDefaultHistoryDateRange();

const defaultContext: IOrderContext = {
  orders: [],
  pendingOrders: [],
  orderHistories: [],
  orderHistoriesCentroid: [],
  ordersAndDealsHistories: [],
  topListingOrders: [],
  orderTypeLabel: EOrderTypeLabel.MARKET,
  orderActionType: null,
  selectedSort: {
    value: EOrderSortOption.OPEN_TIME,
    order: 'desc'
  },
  loadingDetails: true,
  loadingTopListing: false,
  loadingBulkClose: false,
  historyDateRange: defaultHistoryDateRange,
  historyType: EOrderHistoryType.ORDER,

  openOrdersInitialCall: true,
  pendingOrdersInitialCall: true,
  historyOrdersInitialCall: true,
  historyDealsInitialCall: true,
  displayOrders: [],
  displayPendingOrders: [],
  marketOrderCounts: 0,
  pendingOrderCounts: 0,
  disableUpdateTrackOrder: false
};

class OrderService extends Service<IOrderContext> {
  private profitTrackingOrders: IOrder[] = [];
  private ordersSoftRefreshAC: AbortController | undefined = undefined;
  private pendingOrdersSoftRefreshAC: AbortController | undefined = undefined;
  private refreshOrderCountAC: AbortController | undefined = undefined;

  constructor() {
    super();

    /**
     * WT-10221 - Local Storage migration from single boolean to userId-boolean map
     * i.e. from true to { '188...': true }
     */
    let isValidAllSymbolsType: boolean = true;
    try {
      const showAllSymbolsJson = JSON.parse(storage.getItem(EStorageKey.SHOW_ALL_SYMBOLS));
      // Detect if local storage obj is the required type (i.e. userId-boolean map)
      if (
        typeof showAllSymbolsJson === 'object' &&
        Object.values(showAllSymbolsJson).every((value) => typeof value === 'boolean')
      ) {
        isValidAllSymbolsType = true;
      } else {
        isValidAllSymbolsType = false;
      }
    } catch {
      // If Json.parse throws error, the localStorage data is invalid
      isValidAllSymbolsType = false;
    }

    if (!isValidAllSymbolsType) {
      storage.setItem(EStorageKey.SHOW_ALL_SYMBOLS, JSON.stringify({}));
    }

    this.context = {
      ...defaultContext
    };
  }

  public setFilterDisplayOrders = (orderId: string) => {
    const currentDisplayOrders = this.context.displayOrders;
    const newDisplayOrders = currentDisplayOrders.filter((order) => order.orderId !== orderId);
    this.context.displayOrders = newDisplayOrders;
  };

  public setFilterDisplayPendingOrders = (orderId: string) => {
    const currentDisplayOrders = this.context.displayPendingOrders;
    const newDisplayOrders = currentDisplayOrders.filter((order) => order.orderId !== orderId);
    this.context.displayPendingOrders = newDisplayOrders;
  };

  // To manually update the order counts display before the next refresh
  public setDisplayOrderCounts = (
    callback: (counts: {
      marketOrderCounts: number;
      pendingOrderCounts: number;
    }) => Partial<{ marketOrderCounts: number; pendingOrderCounts: number }>
  ) => {
    const { marketOrderCounts, pendingOrderCounts } = this.context;
    const newCounts = callback({ marketOrderCounts, pendingOrderCounts });
    if (newCounts.marketOrderCounts !== undefined) {
      this.context.marketOrderCounts = newCounts.marketOrderCounts;
    }
    if (newCounts.pendingOrderCounts !== undefined) {
      this.context.pendingOrderCounts = newCounts.pendingOrderCounts;
    }
  };

  public setDisplayOrders = (orders: IOrder[]) => {
    this.context.displayOrders = orders;
  };

  public setDisplayPendingOrders = (orders: IPendingOrder[]) => {
    this.context.displayPendingOrders = orders;
  };

  public resetOpenOrdersInitialCall = () => {
    this.context.openOrdersInitialCall = true;
  };

  public resetPendingOrdersInitialCall = () => {
    this.context.pendingOrdersInitialCall = true;
  };

  public resetHistoryOrdersInitialCall = () => {
    this.context.historyOrdersInitialCall = true;
  };

  public resetHistoryOrdersCentroidInitialCall = () => {
    this.context.historyOrdersCentroidInitialCall = true;
  };

  public resetHistoryDealsInitialCall = () => {
    this.context.historyDealsInitialCall = true;
  };

  public resetHistoryDateRange = () => {
    this.context.historyDateRange = getDefaultHistoryDateRange();
  };

  public reset = () => {
    this.context.orders = [];
    this.context.pendingOrders = [];
    this.context.orderHistories = [];
    this.context.ordersAndDealsHistories = [];
    this.resetHistoryDateRange();
  };

  public getTopListing = () => {
    const appType = configService.getAppType();

    this.fetchOpenOrderTopListing(appType === EAppType.PC ? true : this.context.loadingTopListing);
  };

  public onSelectSymbol = (symbol: ISymbol, fetchSymbolDetail = false) => {
    if (fetchSymbolDetail) {
      this.fetchSymbolDetail(symbol.symbol);
    }
  };

  private updateTrackOrderImpl = () => {
    chartService.retrackOrders({
      orders: this.context.orders,
      pendingOrders: this.context.pendingOrders,
      updateTrackers: true
    });
  };

  public updateTrackOrder = debounce(this.updateTrackOrderImpl, 500);

  public setDisableUpdateTrackOrder = (disableUpdate: boolean) => {
    this.context.disableUpdateTrackOrder = disableUpdate;
  };

  public softRefreshOrders = async ({ signal, sort, disableUpdateTrackOrder }: ISoftRefreshOrdersParams = {}) => {
    if (sort) {
      this.context.selectedSort = sort;
    }

    let signalToUse = signal;
    if (!signalToUse) {
      this.ordersSoftRefreshAC?.abort();
      this.ordersSoftRefreshAC = new AbortController();
      signalToUse = this.ordersSoftRefreshAC.signal;
    }

    await this.fetchOpenOrders(signalToUse)
      .then(() => {
        if (!disableUpdateTrackOrder) {
          this.updateTrackOrder();
        }
      })
      .catch(() => {
        // Empty catch block to suppress error
      });
  };

  public softRefreshPendingOrders = ({ signal, sort, disableUpdateTrackOrder }: ISoftRefreshOrdersParams = {}) => {
    if (sort) {
      this.context.selectedSort = sort;
    }

    let signalToUse = signal;
    if (!signalToUse) {
      this.pendingOrdersSoftRefreshAC?.abort();
      this.pendingOrdersSoftRefreshAC = new AbortController();
      signalToUse = this.pendingOrdersSoftRefreshAC.signal;
    }

    this.fetchPendingOrders(signalToUse)
      .then(() => {
        if (!disableUpdateTrackOrder) {
          this.updateTrackOrder();
        }
      })
      .catch(() => {
        // Empty catch block to suppress error
      });
  };

  public softRefreshOrderHistory = (signal?: AbortSignal, sort?: ISortOrderOption) => {
    if (this.context.historyOrdersInitialCall) {
      this.setDefaultSelectedSortForHistory();
    }

    if (sort) {
      this.context.selectedSort = sort;
    }

    try {
      this.fetchOrderHistory(this.context.historyDateRange, signal);
    } catch {
    } finally {
    }
  };

  public softRefreshOrderHistoryCentroid = (signal?: AbortSignal, sort?: ISortOrderOption) => {
    if (this.context.historyOrdersCentroidInitialCall) {
      this.setDefaultSelectedSortForHistoryCentroid();
    }
    if (sort) {
      this.context.selectedSort = sort;
    }

    try {
      this.fetchOrderHistoryCentroid(this.context.historyDateRange, signal);
    } catch {
    } finally {
    }
  };

  public softRefreshOrderDealsHistory = (signal?: AbortSignal, sort?: ISortOrderOption) => {
    if (this.context.historyDealsInitialCall) {
      this.setDefaultSelectedSortForHistory();
    }
    if (sort) {
      this.context.selectedSort = sort;
    }

    try {
      this.fetchOrderDealsHistory(this.context.historyDateRange, signal);
    } catch {
    } finally {
    }
  };

  public setHistoryDateRange = async (
    newHistoryDateRange: IHistoryDateRange,
    tab: TAssetsOrderTab,
    signal?: AbortSignal
  ) => {
    this.context.historyDateRange = newHistoryDateRange;
    switch (tab) {
      case EAssetsOrderTab.ORDERS_AND_DEALS:
        this.resetHistoryDealsInitialCall();
        this.fetchOrderDealsHistory(newHistoryDateRange, signal);
        break;
      case EAssetsOrderTab.ORDER_HISTORY_CENTROID:
        this.resetHistoryOrdersCentroidInitialCall();
        this.fetchOrderHistoryCentroid(newHistoryDateRange, signal);
        break;
      default:
        this.resetHistoryOrdersInitialCall();
        this.fetchOrderHistory(newHistoryDateRange, signal);
    }
  };

  public onChangeHistoryDateRange = (dates: Date[], tab: TAssetsOrderTab, signal?: AbortSignal) => {
    const newHistoryDateRange: IHistoryDateRange = {
      from: dates[0],
      to: dates[1]
    };

    this.context.historyDateRange = newHistoryDateRange;

    switch (tab) {
      case EAssetsOrderTab.ORDER_HISTORY:
      case EAssetsOrderTab.POSITION_HISTORY:
        this.resetHistoryOrdersInitialCall();
        this.fetchOrderHistory(newHistoryDateRange, signal);
        break;
      case EAssetsOrderTab.ORDER_HISTORY_CENTROID:
        this.resetHistoryOrdersCentroidInitialCall();
        this.fetchOrderHistoryCentroid(newHistoryDateRange, signal);
        break;
      case EAssetsOrderTab.ORDERS_AND_DEALS:
        this.resetHistoryDealsInitialCall();
        this.fetchOrderDealsHistory(newHistoryDateRange, signal);
        break;
    }
  };

  public setSymbolDetail = (symbolDetail: ISymbolDetail) => {
    this.context.loadingDetails = false;
    this.context.selectedSymbolDetail = symbolDetail;
  };

  public setSort = (sort: ISortOrderOption, tab: TAssetsOrderTab) => {
    this.context.selectedSort = { ...sort };

    switch (tab) {
      case EAssetsOrderTab.OPEN_POSITION:
        this.sortAndSetOrders(this.profitTrackingOrders, sort);
        break;
      case EAssetsOrderTab.PENDING_ORDER:
        this.sortAndSetPendingOrders(this.context.pendingOrders, sort);
        break;
      case EAssetsOrderTab.ORDER_HISTORY:
      case EAssetsOrderTab.POSITION_HISTORY:
        this.sortAndSetOrderHistories(this.context.orderHistories, sort);
        break;
      case EAssetsOrderTab.ORDER_HISTORY_CENTROID:
        this.sortAndSetOrderHistoriesCentroid(this.context.orderHistoriesCentroid, sort);
        break;
      case EAssetsOrderTab.ORDERS_AND_DEALS:
        this.sortAndSetOrdersAndDealsHistory(this.context.ordersAndDealsHistories, sort);
        break;
    }
  };

  public setOrderTypeLabel = (orderType: EOrderTypeLabel | null) => {
    this.context.orderTypeLabel = orderType;
  };

  public setOrderActionType = (actionType: EOrderActionType | null) => {
    this.context.orderActionType = actionType;
  };

  public updateOrderProfit = (orderId: string, profit?: number) => {
    this.profitTrackingOrders = this.profitTrackingOrders.map((o) => {
      if (o.orderId === orderId && profit) {
        return {
          ...o,
          profit
        };
      }

      return o;
    });
  };

  public getProfitTrackingOrders = () => {
    return this.profitTrackingOrders;
  };

  public closeLimitOrder = async ({ order, onComplete, mixpanelProperties }: ICloseLimitOrderParams) => {
    const { orderId, symbol, id } = order || {};
    const { livePrice } = mixpanelProperties || {};

    const isCentroid = companyService.getIntegration() === EMainProductCodeType.CENTROID;

    try {
      mixpanelAnalysisService.order.deletePendingOrderSubmit({
        order,
        livePrice
      });

      const { result } = await traderAPI.closePendingLimit({
        orderId: isCentroid ? id.toString(10) : orderId,
        symbol
      });

      if (result) {
        // remove the order tracker from chart, if it is tracked
        chartService.cancelTrackOrder(orderId);

        // Exclude the order from the list before starting the websocket fetch to avoid UI display delay.
        this.setFilterDisplayPendingOrders(orderId);
        this.setDisplayOrderCounts(({ pendingOrderCounts }) => {
          return { pendingOrderCounts: pendingOrderCounts - 1 };
        });

        onComplete?.(true, i18n.t('Success'));
      } else {
        onComplete?.(false, i18n.t('Unexpected error'));
      }
    } catch (err) {
      const { code, message } = err as IApiError;

      onComplete?.(false, mapResponseCodeToMessage(code, message));
    }
  };

  public closeMarketOrder = async (params: ICloseMarketOrderParams) => {
    const { onComplete, ...closeMarketParams } = params;

    try {
      const { result } = await traderAPI.closeMarket({ ...closeMarketParams });

      if (result) {
        onComplete?.(true, i18n.t('Success'));
      } else {
        onComplete?.(false, i18n.t('Unexpected error'));
      }
    } catch (err) {
      const { code, message } = err as IApiError;

      onComplete?.(false, mapResponseCodeToMessage(code, message));
    }
  };

  public setHistoryType = (historyType: EOrderHistoryType) => {
    this.context.historyType = historyType;
  };

  private fetchSymbolDetail = async (symbol: string) => {
    try {
      this.context.loadingDetails = true;

      const { result } = await symbolDetailAPI({
        symbol
      });

      if (result) {
        this.context.selectedSymbolDetail = result;
      }
    } catch {
    } finally {
      this.context.loadingDetails = false;
    }
  };

  private fetchOpenOrders = async (signal?: AbortSignal) => {
    try {
      const querySelectedSymbol = this.context.isTradePage && !this.context.showAllSymbols;
      const { result } = await orderAPI.getAll(
        { symbol: querySelectedSymbol ? this.context.selectedSymbolDetail?.symbol : undefined },
        signal
      );
      const newOrders = result || [];

      this.sortAndSetOrders(newOrders, this.context.selectedSort);
      this.profitTrackingOrders = newOrders;
      if (this.context.openOrdersInitialCall) {
        this.context.openOrdersInitialCall = false;
      }
    } catch (error) {
      if (this.context.openOrdersInitialCall && !this.isRequestAbort(error)) {
        this.context.openOrdersInitialCall = false;
      }
      // Rethrow error to caller to differentiate success or failure
      throw error;
    } finally {
    }
  };

  private fetchPendingOrders = async (signal?: AbortSignal) => {
    try {
      const querySelectedSymbol = this.context.isTradePage && !this.context.showAllSymbols;
      const { result } = await pendingAPI.getAll(
        { symbol: querySelectedSymbol ? this.context.selectedSymbolDetail?.symbol : undefined },
        signal
      );

      const pendingOrders = result || [];

      this.sortAndSetPendingOrders(pendingOrders, this.context.selectedSort);
      if (this.context.pendingOrdersInitialCall) {
        this.context.pendingOrdersInitialCall = false;
      }
    } catch (error) {
      if (this.context.pendingOrdersInitialCall && !this.isRequestAbort(error)) {
        this.context.pendingOrdersInitialCall = false;
      }
      // Rethrow error to caller to differentiate success or failure
      throw error;
    } finally {
    }
  };

  private fetchOrderHistory = async (dateRange: IHistoryDateRange, signal?: AbortSignal) => {
    const { epochFrom, epochTo } = convertDateRangeWithUtcOffset(dateRange);

    try {
      if (epochFrom === undefined || epochTo === undefined) {
        return;
      }

      const { result } = await orderAPI.getHistories(
        {
          to: epochTo,
          from: epochFrom,
          type: this.context.historyType
        },
        signal
      );

      this.sortAndSetOrderHistories(result || [], this.context.selectedSort);

      if (this.context.historyOrdersInitialCall) {
        this.context.historyOrdersInitialCall = false;
      }
    } catch (error) {
      if (this.context.historyOrdersInitialCall && !this.isRequestAbort(error)) {
        this.context.historyOrdersInitialCall = false;
      }
    } finally {
    }
  };

  private fetchOrderHistoryCentroid = async (dateRange: IHistoryDateRange, signal?: AbortSignal) => {
    const { epochFrom, epochTo } = convertDateRangeWithUtcOffset(dateRange);
    try {
      if (epochFrom === undefined || epochTo === undefined) {
        return;
      }

      const { result } = await orderAPI.getHistories(
        {
          to: epochTo,
          from: epochFrom,
          type: EOrderHistoryType.ORDER
        },
        signal
      );

      this.sortAndSetOrderHistoriesCentroid(result || [], this.context.selectedSort);

      if (this.context.historyOrdersCentroidInitialCall) {
        this.context.historyOrdersCentroidInitialCall = false;
      }
    } catch (error) {
      if (this.context.historyOrdersCentroidInitialCall && !this.isRequestAbort(error)) {
        this.context.historyOrdersCentroidInitialCall = false;
      }
    } finally {
    }
  };

  private fetchOrderDealsHistory = async (dateRange: IHistoryDateRange, signal?: AbortSignal) => {
    const { epochFrom, epochTo } = convertDateRangeWithUtcOffset(dateRange);

    try {
      if (epochFrom === undefined || epochTo === undefined) {
        return;
      }

      const { result } = await dealHistories({ to: epochTo, from: epochFrom }, signal);

      this.sortAndSetOrdersAndDealsHistory(result || [], this.context.selectedSort);

      if (this.context.historyDealsInitialCall) {
        this.context.historyDealsInitialCall = false;
      }
    } catch (error) {
      if (this.context.historyDealsInitialCall && !this.isRequestAbort(error)) {
        this.context.historyDealsInitialCall = false;
      }
    } finally {
    }
  };

  private sortAndSetOrders = (orders: IOrder[], sort: ISortOrderOption) => {
    // Prevent mutation of the original orders
    const ordersToSort = [...orders];
    switch (sort.value) {
      case EOrderSortOption.PROFIT:
        this.context.orders = [...orderBy(ordersToSort, 'profit', sort.order)];
        break;
      case EOrderSortOption.OPEN_TIME:
        this.context.orders = [...orderBy(ordersToSort, 'openTime', sort.order)];
        break;
      case EOrderSortOption.SYMBOL:
        this.context.orders = [...orderBy(ordersToSort, 'symbol', sort.order)];
        break;
      default:
        this.context.orders = ordersToSort;
        break;
    }
  };

  private sortAndSetPendingOrders = (pendingOrders: IPendingOrder[], sort: ISortOrderOption) => {
    // Prevent mutation of the original pending orders
    const pendingOrdersToSort = [...pendingOrders];
    switch (sort.value) {
      case EOrderSortOption.OPEN_TIME:
        this.context.pendingOrders = [...orderBy(pendingOrdersToSort, 'openTime', sort.order)];
        break;
      case EOrderSortOption.SYMBOL:
        this.context.pendingOrders = [...orderBy(pendingOrdersToSort, 'symbol', sort.order)];
        break;
      default:
        this.context.pendingOrders = pendingOrdersToSort;
        break;
    }
  };

  private sortAndSetOrdersAndDealsHistory = (ordersAndDealsHistories: IDealHistory[], sort: ISortOrderOption) => {
    // Prevent mutation of the original orders and deals histories
    const ordersAndDealsHistoriesToSort = [...ordersAndDealsHistories];
    switch (sort.value) {
      case EOrderSortOption.OPEN_TIME:
        this.context.ordersAndDealsHistories = [...orderBy(ordersAndDealsHistoriesToSort, 'openTime', sort.order)];
        break;
      case EOrderSortOption.SYMBOL:
        this.context.ordersAndDealsHistories = [...orderBy(ordersAndDealsHistoriesToSort, 'symbol', sort.order)];
        break;
      case EOrderSortOption.CLOSE_TIME:
        this.context.ordersAndDealsHistories = [...orderBy(ordersAndDealsHistoriesToSort, 'closeTime', sort.order)];
        break;
      default:
        this.context.ordersAndDealsHistories = ordersAndDealsHistoriesToSort;
        break;
    }
  };

  private sortAndSetOrderHistories = (orderHistories: IOrderHistory[], sort: ISortOrderOption) => {
    // Prevent mutation of the original orders histories
    const orderHistoriesToSort = [...orderHistories];
    switch (sort.value) {
      case EOrderSortOption.PROFIT:
        this.context.orderHistories = [...orderBy(orderHistoriesToSort, 'profit', sort.order)];
        break;
      case EOrderSortOption.SYMBOL:
        this.context.orderHistories = [...orderBy(orderHistoriesToSort, 'symbol', sort.order)];
        break;
      case EOrderSortOption.OPEN_TIME:
        this.context.orderHistories = [...orderBy(orderHistoriesToSort, 'openTime', sort.order)];
        break;
      case EOrderSortOption.CLOSE_TIME:
        this.context.orderHistories = [...orderBy(orderHistoriesToSort, 'closeTime', sort.order)];
        break;
      default:
        this.context.orderHistories = orderHistoriesToSort;
        break;
    }
  };

  private sortAndSetOrderHistoriesCentroid = (orderHistoriesCentroid: IOrderHistory[], sort: ISortOrderOption) => {
    // Prevent mutation of the original orders centroid histories
    const orderHistoriesCentroidToSort = [...orderHistoriesCentroid];
    switch (sort.value) {
      case EOrderSortOption.SYMBOL:
        this.context.orderHistoriesCentroid = [...orderBy(orderHistoriesCentroidToSort, 'symbol', sort.order)];
        break;
      case EOrderSortOption.OPEN_TIME:
        this.context.orderHistoriesCentroid = [...orderBy(orderHistoriesCentroidToSort, 'openTime', sort.order)];
        break;
      default:
        this.context.orderHistoriesCentroid = orderHistoriesCentroidToSort;
        break;
    }
  };

  private fetchOpenOrderTopListing = async (loading = true) => {
    try {
      this.context.loadingTopListing = loading;

      const { result } = await orderAPI.getAll();

      const newOrders = result || [];

      this.context.topListingOrders = [...orderBy(newOrders, 'openTime', 'desc')];
    } catch {
    } finally {
      this.context.loadingTopListing = false;
    }
  };

  private setDefaultSelectedSortForHistory = () => {
    this.context.selectedSort = {
      value: companyService.getIntegration() === 'CENTROID' ? EOrderSortOption.OPEN_TIME : EOrderSortOption.CLOSE_TIME,
      order: 'desc'
    };
  };

  private setDefaultSelectedSortForHistoryCentroid = () => {
    this.context.selectedSort = {
      value: EOrderSortOption.OPEN_TIME,
      order: 'desc'
    };
  };

  // Filter orders base on its profit status
  private filterOrdersByProfitStatus(orders: IOrder[], orderProfitStatus?: EOrderProfitStatus): IOrder[] {
    switch (orderProfitStatus) {
      case EOrderProfitStatus.PROFIT:
        return orders.filter((o) => o.profit > 0);
      case EOrderProfitStatus.LOSS:
        return orders.filter((o) => o.profit < 0);
      default:
        return orders;
    }
  }

  // To determine the params inside the order object, MT5 and MT4 have different params
  private createCloseMarketOrderParam(order: IOrder, isMT5: boolean): ICloseMarketTradeParams {
    const closeMarketParam: ICloseMarketTradeParams = {
      orderId: order.orderId,
      symbol: order.symbol,
      lotSize: order.lotSize
    };
    if (isMT5) {
      if (!order.marketFillPolicy || order.marketFillPolicy.length === 0) {
        closeMarketParam.fillPolicy = ETradeFillPolicy.FOK;
      } else if (order.marketFillPolicy.includes(order.fillPolicy)) {
        closeMarketParam.fillPolicy = order.fillPolicy;
      } else {
        closeMarketParam.fillPolicy = order.marketFillPolicy[0];
      }
    }
    return closeMarketParam;
  }

  public bulkCloseMarket = async (params: {
    ordersMaxSize: number;
    onComplete: (success: boolean, message: string) => void;
    assetPage?: boolean;
    isWeb?: boolean;
    selectedSymbolDetail?: ISymbolDetail;
    orderProfitStatus?: EOrderProfitStatus;
  }) => {
    const { ordersMaxSize, onComplete, assetPage, isWeb, selectedSymbolDetail, orderProfitStatus } = params;
    const isTradePageAndSelectedSymbolOnly = !assetPage && selectedSymbolDetail && !this.context.showAllSymbols;

    /**
     * Retrieve the present orders according to the platform type.
     * If the platform is web, it obtains the displayed orders from the context.
     * A maximum of 30 orders is allowed on both the asset and other pages. In the Trading Page, orders will be filtered by symbol, e.g., orders for XRP only.
     * If the platform is not web, it retrieves a slice of the orders from the context, up to a maximum size. Currently mobile display all orders in the Asset Page
     * @returns An array of current orders.
     */

    // In mobile asset page, display all orders
    let mobileOrders = this.context.orders;

    // In mobile trade page and Show All Symbols are checked, display max 30 orders for the selected symbol
    if (isTradePageAndSelectedSymbolOnly) {
      mobileOrders = this.context.orders
        .filter((o) => o.symbol === selectedSymbolDetail.symbol)
        .slice(0, ordersMaxSize);
    }

    const currentOrders = isWeb ? this.context.displayOrders : mobileOrders;
    const profitTrackingOrders = this.profitTrackingOrders; // Orders with live profit and loss

    if (currentOrders.length > 0) {
      this.context.loadingBulkClose = true;
      const isMT5 = companyService.getIntegration() === EMainProductCodeType.METATRADER5;

      const orders: IOrder[] = profitTrackingOrders.filter((o) => {
        return currentOrders.some((d) => d.orderId === o.orderId);
      });

      const filteredOrders = this.filterOrdersByProfitStatus(orders, orderProfitStatus);

      const orderList: ICloseMarketTradeParams[] = [];

      filteredOrders.forEach((order) => {
        if (orderList.length >= ordersMaxSize) {
          return;
        }

        // In mobile, We will need to check the orders that are closable and check if the symbol is selected
        // In web , the display order already check if the orders is from asset page (multiple symbol) or trading page (single symbol)
        const shouldPush =
          !isWeb && isTradePageAndSelectedSymbolOnly
            ? order.symbol === selectedSymbolDetail.symbol && order.closable
            : order.closable;

        if (shouldPush) {
          orderList.push(this.createCloseMarketOrderParam(order, isMT5));
        }
      });

      mixpanelAnalysisService.order.bulkClosePositionsSubmit({
        symbolDetails: isTradePageAndSelectedSymbolOnly ? selectedSymbolDetail : undefined,
        numOfPositions: orderList.length,
        bulkCloseType: orderProfitStatus ? mapOrderProfitStatusToBulkCloseType(orderProfitStatus) : undefined
      });

      await traderAPI
        .bulkCloseMarket({ orderList })
        .then((response) => {
          const { result } = response;
          onComplete(true, this.bulkCloseSuccessMessage(EAssetsOrderTab.OPEN_POSITION, result?.closedOrderIds));
        })
        .catch((err) => {
          const { code, message } = err as IApiError;
          onComplete?.(false, mapResponseCodeToMessage(code, message));
        })
        .finally(() => {
          this.context.loadingBulkClose = false;
          this.debouncedRefreshOrders(isWeb)({ refreshOpenOrders: true });
        });
    }
  };

  public bulkDeletePending = async (params: {
    ordersMaxSize: number;
    onComplete: (success: boolean, message: string) => void;
    assetPage?: boolean;
    isWeb?: boolean;
    selectedSymbolDetail?: ISymbolDetail;
  }) => {
    const { ordersMaxSize, onComplete, assetPage, isWeb, selectedSymbolDetail } = params;
    const isCentroid = companyService.getIntegration() === EMainProductCodeType.CENTROID;
    const isTradePageAndSelectedSymbolOnly = !assetPage && selectedSymbolDetail && !this.context.showAllSymbols;

    const pendingOrders = isWeb ? this.context.displayPendingOrders : this.context.pendingOrders;
    if (pendingOrders.length > 0) {
      this.context.loadingBulkClose = true;

      const orderIdList: IBulkDeletePendingRequest[] = [];
      pendingOrders.forEach((order) => {
        if (orderIdList.length >= ordersMaxSize) {
          return;
        }

        const shouldPush =
          !isWeb && isTradePageAndSelectedSymbolOnly
            ? order.symbol === selectedSymbolDetail.symbol && order.closable
            : order.closable;

        if (shouldPush) {
          orderIdList.push({ orderId: isCentroid ? order.id.toString(10) : order.orderId, symbol: order.symbol });
        }
      });

      mixpanelAnalysisService.order.bulkDeletePendingOrdersSubmit({
        symbolDetails: isTradePageAndSelectedSymbolOnly ? selectedSymbolDetail : undefined,
        numOfPositions: orderIdList.length
      });

      await traderAPI
        .bulkDeletePending(orderIdList)
        .then((response) => {
          const { result } = response;
          onComplete(true, this.bulkCloseSuccessMessage(EAssetsOrderTab.PENDING_ORDER, result?.deletedOrderIds));
        })
        .catch((err) => {
          const { code, message } = err as IApiError;
          onComplete?.(false, mapResponseCodeToMessage(code, message));
        })
        .finally(() => {
          this.context.loadingBulkClose = false;
          this.debouncedRefreshOrders(isWeb)();
        });
    }
  };

  public webSocketOrBulkCloseRefreshOrders = async (params?: IWebSocketOrBulkCloseRefreshOrdersParams) => {
    const { refreshOpenOrders, refreshOrderHistory } = params || {};

    // Do not refresh the orders when bulk close is loading
    if (this.context.loadingBulkClose) {
      return;
    }

    const integration = companyService.getIntegration();
    const isMT5 = integration === EMainProductCodeType.METATRADER5;
    const isCentroid = integration === EMainProductCodeType.CENTROID;

    this.getOrderCounts(this.context.isTradePage ?? false);
    if (refreshOpenOrders) {
      this.resetOpenOrdersInitialCall();
      await this.softRefreshOrders({ disableUpdateTrackOrder: this.context.disableUpdateTrackOrder });
    }

    if (!refreshOpenOrders || !isMT5) {
      this.resetPendingOrdersInitialCall();
      this.softRefreshPendingOrders({ disableUpdateTrackOrder: this.context.disableUpdateTrackOrder });
    }

    if (refreshOrderHistory) {
      this.resetHistoryOrdersInitialCall();
      this.setHistoryType(isMT5 || isCentroid ? EOrderHistoryType.POSITION : EOrderHistoryType.ORDER);
      this.softRefreshOrderHistory();
      if (isMT5) {
        this.resetHistoryDealsInitialCall();
        this.softRefreshOrderDealsHistory();
      }
      if (isCentroid) {
        this.resetHistoryOrdersCentroidInitialCall();
        this.softRefreshOrderHistoryCentroid();
      }
    }

    statisticsService.refreshStatistics();
    this.setDisableUpdateTrackOrder(false);
  };

  public debouncedRefreshOrders = (isWeb?: boolean) => {
    return debounce(
      (params?: IWebSocketOrBulkCloseRefreshOrdersParams) => {
        this.webSocketOrBulkCloseRefreshOrders(params);
      },
      isWeb ? 300 : 750
    );
  };

  private bulkCloseSuccessMessage = (selectedTab: EAssetsOrderTab, closedOrderIds?: number[]) => {
    let message = '';
    if (closedOrderIds && closedOrderIds.length > 0) {
      if (closedOrderIds.length > 3) {
        const firstThreeOrder = closedOrderIds.slice(0, 3);
        const otherOrdersCount = closedOrderIds.length - 3;

        const messageParam = {
          orderNumber: '#' + firstThreeOrder.join(', #'),
          ordersCount: otherOrdersCount
        };

        message =
          selectedTab === EAssetsOrderTab.OPEN_POSITION
            ? i18n.t('Positions {{orderNumber}} and {{ordersCount}} others have been closed.', messageParam)
            : i18n.t('Pending orders {{orderNumber}} and {{ordersCount}} others have been deleted.', messageParam);
      } else {
        const messageParam = {
          orderNumber: '#' + closedOrderIds.join(', #')
        };
        if (closedOrderIds.length > 1) {
          message =
            selectedTab === EAssetsOrderTab.OPEN_POSITION
              ? i18n.t('Positions {{orderNumber}} have been closed.', messageParam)
              : i18n.t('Pending orders {{orderNumber}} have been deleted.', messageParam);
        } else {
          message =
            selectedTab === EAssetsOrderTab.OPEN_POSITION
              ? i18n.t('Position {{orderNumber}} has been closed.', messageParam)
              : i18n.t('Pending order {{orderNumber}} has been deleted.', messageParam);
        }
      }
    }
    return message;
  };

  public checkBulkDeletePerformable = (param: {
    orders: (IOrder | IPendingOrder)[];
    symbol?: string;
    assetPage?: boolean;
    closable?: boolean;
  }) => {
    const { orders, symbol, assetPage, closable } = param;

    // If Show All Symbols checkbox is unchecked and market is closed, return false (i.e. cannot bulk close/delete)
    if (!assetPage && !closable && !this.context.showAllSymbols) {
      return false;
    }

    /**
     * Determine if
     * (1) current page is trade page, and
     * (2) symbol is defined (i.e. only on Trade page), and
     * (3) Show All Symbols is checked (i.e. should delete only positions/orders that match the input symbol)
     */
    const isTradePageAndSelectedSymbolOnly = !assetPage && symbol && !this.context.showAllSymbols;
    const ordersCount = (isTradePageAndSelectedSymbolOnly
      ? orders.filter((o: IOrder | IPendingOrder) => o.symbol === symbol && o.closable === true)
      : orders.filter((o: IOrder | IPendingOrder) => o.closable === true)
    )?.length;

    return ordersCount > 0;
  };

  public updateTopListingForWatch = (symbol: string, watch: boolean) => {
    try {
      const { topListingOrders } = this.context;

      this.context.topListingOrders = topListingOrders.map((s) => {
        if (s.symbol === symbol) {
          return {
            ...s,
            watch
          };
        }

        return s;
      });
    } catch (error) {}
  };

  public getOrderCounts = async (isTradePage: boolean, signal?: AbortSignal) => {
    try {
      let symbol: string | undefined;
      if (isTradePage && !this.context.showAllSymbols) {
        symbol = this.context.selectedSymbolDetail?.symbol;

        if (!symbol) {
          return;
        }
      }

      let signalToUse = signal;
      if (!signalToUse) {
        this.refreshOrderCountAC?.abort();
        this.refreshOrderCountAC = new AbortController();
        signalToUse = this.refreshOrderCountAC.signal;
      }

      const { result } = await orderAPI.getOrderCounts({ symbol }, signalToUse);
      const { marketOrderCounts, pendingOrderCounts } = result || {};

      this.context.marketOrderCounts = marketOrderCounts ?? 0;
      this.context.pendingOrderCounts = pendingOrderCounts ?? 0;
    } catch (error) {
      throw error;
    }
  };

  public setIsTradePage = (isTradePage?: boolean) => {
    this.context.isTradePage = isTradePage;
  };

  public initShowAllSymbols = (userId: number) => {
    let showAllSymbols = false;
    try {
      const showAllSymbolsJson = JSON.parse(storage.getItem(EStorageKey.SHOW_ALL_SYMBOLS));
      if (typeof showAllSymbolsJson === 'object' && showAllSymbolsJson.hasOwnProperty(userId.toString())) {
        // force variable into boolean in case there is a deviation in data type
        showAllSymbols = Boolean(showAllSymbolsJson[userId.toString()]);
      }
    } catch {
      showAllSymbols = false;
    }
    this.context.showAllSymbols = showAllSymbols;
  };

  public setShowAllSymbols = (showAllSymbols: boolean, userId: number | undefined) => {
    if (!userId) {
      return;
    }

    this.context.showAllSymbols = showAllSymbols;

    const showAllSymbolsJson = JSON.parse(storage.getItem(EStorageKey.SHOW_ALL_SYMBOLS) ?? '{}');
    const showAllSymbolsFinal = {
      ...showAllSymbolsJson,
      [userId]: showAllSymbols
    };
    storage.setItem(EStorageKey.SHOW_ALL_SYMBOLS, JSON.stringify(showAllSymbolsFinal));
  };

  public clearShowAllSymbols = () => {
    this.context.showAllSymbols = false;
  };

  public resetOrderCounts = async () => {
    this.context.marketOrderCounts = 0;
    this.context.pendingOrderCounts = 0;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isRequestAbort = (error: any) => {
    const { status } = error || {};

    // Our custom status for request aborted
    return status && status === 'request_aborted';
  };

  public calculateRiskRewards = (params: ICalculateRiskRewardsParams) => {
    const { potentialGain, potentialLoss } = params;
    if (potentialLoss && potentialGain) {
      const potentialGainBN = new BigNumber(removeThousandSeparator(potentialGain)).abs();
      const potentialLossBN = new BigNumber(removeThousandSeparator(potentialLoss)).abs();
      return '1 : ' + potentialGainBN.div(potentialLossBN).toFixed(2);
    }
    return undefined;
  };
}

export default new OrderService();
